import { Flex } from 'antd';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  DefaultIconButton,
  Heading,
  Radio,
  RadioGroup,
  Spin,
  Text,
  Tooltip,
} from '../Ant';
import { Close } from '../icons';
import { useTranslation } from 'react-i18next';
import { ITransactionFilter } from '../../interface/transaction';

interface FilterOption {
  label: string;
  value: string;
}

interface FilterPopoverContentProps {
  popoverTitle?: string;
  primarytitle?: string;
  secondarytitle?: string;
  ownerTitle?: string;
  ownerOptions?: FilterOption[];
  radioOptions?: FilterOption[];
  checkboxOptions?: FilterOption[];
  onClose: () => void;
  onClear: () => void;
  selectedRadio: string | null;
  setSelectedRadio: (value: string | null) => void;
  selectedOwner?: string | null;
  setSelectedOwner?: (value: string | null) => void;
  setOwnerValue?: (val: ITransactionFilter) => void;
  tempSelectedValue: ITransactionFilter | null;
  setTempSelectedValue: (value: ITransactionFilter) => void;
  selectedCheckboxes: string[];
  setSelectedCheckboxes: (value: string[]) => void;
  disabled?: boolean;
  disabledCheckboxValues?: string[];
  disabledRadioValues?: string[];
  disabledInfoMessage?: string;
  setCheckBoxValues?: (val: ITransactionFilter[]) => void;
  setSelectedValue?: (val: ITransactionFilter) => void;
  hasMoreCheckboxOptions?: boolean;
  isFetchingMoreCheckboxOptions?: boolean;
  onLoadMoreCheckboxOptions?: () => void;
}

const FilterPopoverContent: React.FC<FilterPopoverContentProps> = ({
  popoverTitle,
  primarytitle,
  secondarytitle,
  ownerTitle,
  ownerOptions = [],
  radioOptions = [],
  checkboxOptions = [],
  onClose,
  onClear,
  selectedRadio,
  setSelectedRadio,
  selectedOwner = null,
  setSelectedOwner,
  setOwnerValue,
  tempSelectedValue,
  setTempSelectedValue,
  selectedCheckboxes,
  setSelectedCheckboxes,
  disabled = false,
  disabledCheckboxValues = [],
  disabledRadioValues = [],
  disabledInfoMessage,
  setCheckBoxValues,
  setSelectedValue,
  hasMoreCheckboxOptions = false,
  isFetchingMoreCheckboxOptions = false,
  onLoadMoreCheckboxOptions,
}) => {
  const { t } = useTranslation();
  const isFullyLocked = disabled && disabledRadioValues.length === 0;
  const { ref: checkboxScrollRef, inView: isCheckboxSentinelInView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (isCheckboxSentinelInView && hasMoreCheckboxOptions && !isFetchingMoreCheckboxOptions) {
      onLoadMoreCheckboxOptions?.();
    }
  }, [isCheckboxSentinelInView, hasMoreCheckboxOptions, isFetchingMoreCheckboxOptions]);

  return (
    <Flex vertical gap={15}>
      <Flex align="center" justify="space-between">
        <Heading
          data-testid="filter-popover-title"
          level={3}
          $color="title"
          $fontsize="14px"
          $weight={600}
        >
          {popoverTitle}
        </Heading>
        <DefaultIconButton
          data-testid="filter-close-button"
          aria-label={t('common:bottomNavigation.close')}
          onClick={onClose}
          type="text"
          icon={<Close className="fz-12" />}
        />
      </Flex>
      <div className="popover-container d-flex flex-col gap-15">
        {ownerOptions.length > 0 && (
          <div className="d-flex flex-col gap-10">
            {ownerTitle && (
              <Text
                data-testid="filter-owner-title"
                $fontsize="12px"
                $color="description"
                $weight={500}
              >
                {ownerTitle}
              </Text>
            )}
            <RadioGroup
              value={selectedOwner}
              data-testid="filter-owner-radio-group"
              onChange={(e) => setSelectedOwner?.(e.target.value)}
              className="d-flex fd-column gap-10 filter-radio-group"
            >
              {ownerOptions.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  data-testid={`filter-owner-radio-${option.value}`}
                >
                  <Text $fontsize="13px" $color="title" $weight={600} $lineheight="18px">
                    {option.label}
                  </Text>
                </Radio>
              ))}
            </RadioGroup>
          </div>
        )}

        {(primarytitle || radioOptions.length > 0) && (
          <div className="d-flex flex-col gap-10">
            {primarytitle && (
              <Text
                data-testid="filter-primary-title"
                $fontsize="12px"
                $color="description"
                $weight={500}
              >
                {primarytitle}
              </Text>
            )}
            {radioOptions.length > 0 && (
              <RadioGroup
                value={selectedRadio}
                data-testid="filter-radio-group"
                onChange={(e) => {
                  if (disabledRadioValues.includes(e.target.value)) return;
                  // With no explicit disabledRadioValues, `disabled` alone means "lock every
                  // option except the current selection". Once specific values are supplied,
                  // they are the sole source of truth so other options stay pickable.
                  if (isFullyLocked && e.target.value !== selectedRadio) return;
                  setSelectedRadio(e.target.value);
                  const selectedOption = radioOptions.find(
                    (option) => option.value === e.target.value,
                  );
                  if (selectedOption) {
                    setTempSelectedValue(selectedOption);
                  }
                }}
                className={`d-flex fd-column gap-10 filter-radio-group ${isFullyLocked ? 'semi-disabled' : ''}`}
              >
                {radioOptions.map((option) => {
                  const radio = (
                    <Radio
                      key={option.value}
                      value={option.value}
                      disabled={disabledRadioValues.includes(option.value)}
                      data-testid={`filter-radio-${option.value}`}
                    >
                      <Text $fontsize="13px" $color="title" $weight={600} $lineheight="18px">
                        {option.label}
                      </Text>
                    </Radio>
                  );
                  if (!isFullyLocked || !disabledInfoMessage) return radio;
                  return (
                    <Tooltip
                      key={option.value}
                      title={disabledInfoMessage}
                      placement="top"
                      arrow={false}
                    >
                      {radio}
                    </Tooltip>
                  );
                })}
              </RadioGroup>
            )}
          </div>
        )}

        {(secondarytitle || checkboxOptions.length > 0) && (
          <div className="d-flex flex-col gap-10">
            {secondarytitle && (
              <Text $fontsize="12px" $color="description" $weight={500}>
                {secondarytitle}
              </Text>
            )}
            {checkboxOptions.length > 0 && (
              <div className="d-flex flex-col gap-10 filter-checkbox-list">
                <CheckboxGroup
                  value={selectedCheckboxes}
                  onChange={(checkedValues) => {
                    const newValues = [
                      ...selectedCheckboxes.filter((val) => disabledCheckboxValues.includes(val)),
                      ...(checkedValues as string[]).filter(
                        (val) => !disabledCheckboxValues.includes(val),
                      ),
                    ];
                    setSelectedCheckboxes(newValues);
                  }}
                  className="d-flex flex-col gap-10"
                  data-testid="filter-checkbox-group"
                >
                  {checkboxOptions.map((option) => (
                    <Checkbox
                      key={option.value}
                      value={option.value}
                      className={`custom-checkbox ${disabledCheckboxValues.includes(option.value) ? 'semi-disabled' : ''}`}
                      data-testid={`filter-checkbox-${option.value}`}
                    >
                      <Text $fontsize="13px" $color="title" $weight={600} $lineheight="18px">
                        {option.label}
                      </Text>
                    </Checkbox>
                  ))}
                </CheckboxGroup>
                {hasMoreCheckboxOptions && (
                  <div
                    ref={checkboxScrollRef}
                    className="loading-indicator"
                    data-testid="checkbox-options-loading-indicator"
                  />
                )}
                {isFetchingMoreCheckboxOptions && (
                  <Spin size="small" data-testid="checkbox-options-fetching-indicator" />
                )}
              </div>
            )}
          </div>
        )}

        <div className="d-flex gap-5 w-full">
          <Button
            onClick={() => {
              onClose();
              onClear();
            }}
            data-testid="clear-filters-button"
            className="w-full"
            maxwidth="100px"
          >
            {t('common:clear')}
          </Button>
          <Button
            type="primary"
            className="w-full"
            data-testid="apply-filters-button"
            onClick={() => {
              const selectedOptions = checkboxOptions?.filter(
                (option) =>
                  selectedCheckboxes.includes(option.value) &&
                  !disabledCheckboxValues.includes(option.value),
              );
              setCheckBoxValues?.(selectedOptions);
              setSelectedValue?.(tempSelectedValue as ITransactionFilter);
              const selectedOwnerOption = ownerOptions.find(
                (option) => option.value === selectedOwner,
              );
              if (selectedOwnerOption) setOwnerValue?.(selectedOwnerOption);
              onClose();
            }}
          >
            {t('common:apply')}
          </Button>
        </div>
      </div>
    </Flex>
  );
};

export default FilterPopoverContent;
